import { useEffect, useState } from 'react';
import { Check, ChevronDown, ChevronRight, CircleAlert, Wrench } from 'lucide-react';
import type { ChatMessage as ChatMessageType, ToolLifecycleStatus } from '../../types';
import { ACTIVITY_SETTLE_DELAY_MS, useDelayedSettle } from '@/hooks/use-delayed-settle';

interface AgentActivityTimelineProps {
  /** Consecutive tool delta messages for one run, already in logical order. */
  messages: ChatMessageType[];
  /** True while this group's turn is still streaming (keeps the stack expanded). */
  live?: boolean;
}

const cardStatus = (message: ChatMessageType): ToolLifecycleStatus =>
  message.toolStatus ?? (message.finalizedAt ? 'success' : 'running');

const StatusIcon = ({ status }: { status: ToolLifecycleStatus }) => {
  if (status === 'success') {
    return <Check className="mt-0.5 h-4 w-4 text-muted-foreground" aria-label="completed" />;
  }
  if (status === 'error') {
    return <CircleAlert className="mt-0.5 h-4 w-4 text-destructive" aria-label="failed" />;
  }
  return <Wrench className="mt-0.5 h-4 w-4 text-muted-foreground" aria-label="running" />;
};

const resolveToolLabel = (message: ChatMessageType): string | null => {
  if (message.toolLabel) return message.toolLabel;
  if (message.toolName) {
    console.error(
      `[AGENT ACTIVITY] Missing toolLabel for tool ${message.toolName} (message ${message.id})`
    );
  }
  return null;
};

const buildSummary = (messages: ChatMessageType[]): string => {
  const labels = messages
    .map((m) => resolveToolLabel(m))
    .filter((l): l is string => Boolean(l));
  const unique = [...new Set(labels)];
  const steps = `${messages.length} step${messages.length === 1 ? '' : 's'}`;
  if (!unique.length) return steps;
  const shown = unique.slice(0, 3).join(', ');
  const more = unique.length > 3 ? ` +${unique.length - 3} more` : '';
  return `${steps} · ${shown}${more}`;
};

/**
 * Tool lifecycle cards for a single agent turn.
 *
 * Each card is keyed by its block id (stable across (round, call_index)) and
 * mutates in place: running pulse, then check or error icon from the
 * tool_result lifecycle event. The stack collapses to a one-line summary on
 * its own timeline — a few seconds after its last tool finishes, independent
 * of the turn completing (immediately on history reload). Errors keep the
 * stack expanded.
 */
export const AgentActivityTimeline = ({ messages, live = false }: AgentActivityTimelineProps) => {
  const hasError = messages.some((m) => cardStatus(m) === 'error');
  const finished = messages.length > 0 && messages.every((m) => cardStatus(m) !== 'running');
  const complete = useDelayedSettle(finished, ACTIVITY_SETTLE_DELAY_MS);
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);

  // Reset manual expansion when the turn goes live again (new tools streaming in).
  useEffect(() => {
    if (live) setUserExpanded(null);
  }, [live]);

  if (!messages.length) return null;

  const collapsed = userExpanded === null ? complete && !hasError : !userExpanded;

  return (
    <div>
      {complete && (
        <button
          type="button"
          className="agent-block-enter flex w-full items-center gap-2 text-left text-xs text-muted-foreground transition-opacity hover:opacity-80"
          onClick={() => setUserExpanded(collapsed)}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Show agent activity details' : 'Collapse agent activity details'}
        >
          {collapsed ? (
            <ChevronRight className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          )}
          {hasError ? (
            <CircleAlert className="h-3.5 w-3.5 shrink-0 text-destructive" />
          ) : (
            <Check className="h-3.5 w-3.5 shrink-0" />
          )}
          <span className="truncate font-mono">{buildSummary(messages)}</span>
        </button>
      )}
      <div className="agent-activity-collapse" data-open={!collapsed}>
        <div className={`min-h-0 overflow-hidden ${complete ? 'pt-1' : ''}`}>
          <div className="flex flex-col" role="list" aria-label="Agent tool activity">
            {messages.map((message, idx) => {
              const isLast = idx === messages.length - 1;
              const status = cardStatus(message);
              const toolLabel = resolveToolLabel(message);

              return (
                <div
                  key={message.id}
                  className="agent-block-enter flex items-stretch gap-2 text-xs"
                  role="listitem"
                >
                  <div className="flex w-4 flex-shrink-0 flex-col items-center">
                    <StatusIcon status={status} />
                    {!isLast && <div className="mt-1 w-px flex-1 bg-border" />}
                  </div>
                  <div className={`flex-1 min-w-0 ${isLast ? '' : 'pb-2'}`}>
                    {toolLabel && (
                      <span
                        className={`mr-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 font-mono text-[10px] ${
                          status === 'error'
                            ? 'bg-destructive/10 text-destructive'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {toolLabel}
                      </span>
                    )}
                    <span className="text-muted-foreground font-mono leading-relaxed">
                      {message.content}
                    </span>
                    {status === 'error' && message.toolError && (
                      <div className="mt-0.5 text-[11px] text-destructive/90">
                        {message.toolError}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
